import type * as SentryType from "@sentry/react"

import { log } from "./console-logging"
import { toLogRecord } from "./log-data"
import {
  normalizeError,
  serializeError,
} from "./error-serializer"
import {
  buildEventFingerprint,
  isBenignHttpError,
  isIgnoredErrorMessage,
  shouldDedupeEvent,
} from "./sentry-filters"
import { scrubTags, scrubValue } from "./sentry-privacy"
import type { SeverityLevel } from "./types"

let sentryModule: typeof SentryType | null = null
let sentryEnabled = false

type CaptureContext = {
  tags?: Record<string, string>
  extra?: Record<string, unknown>
  level?: SeverityLevel
  fingerprint?: string[]
  contexts?: Record<string, Record<string, unknown>>
  /** Report HTTP 401/403/404 on auth flows instead of treating them as benign noise. */
  skipBenignCheck?: boolean
  /** Override the Sentry issue title/message while preserving the original error. */
  message?: string
}

type ScopeLike = {
  setLevel: (level: SeverityLevel) => void
  setExtra: (key: string, value: unknown) => void
  setTag: (key: string, value: string) => void
}

export function markSentryInitialized(module: typeof SentryType) {
  sentryModule = module
  sentryEnabled = true
}

export function isSentryEnabled() {
  return sentryEnabled
}

export function getSentry(): typeof SentryType | null {
  return sentryModule
}

function toError(error: unknown): Error {
  return normalizeError(error)
}

function shouldSkipCapture(error: Error, context?: CaptureContext): boolean {
  if (isIgnoredErrorMessage(error.message)) return true
  if (!context?.skipBenignCheck && isBenignHttpError(error)) return true

  const fingerprint = buildEventFingerprint([
    "exception",
    context?.tags?.feature,
    context?.tags?.api_endpoint,
    context?.tags?.http_status,
    error.name,
    error.message,
  ])

  return shouldDedupeEvent(fingerprint)
}

export function captureException(error: unknown, context?: CaptureContext) {
  const serialized = serializeError(error)
  const normalized = toError(error)

  if (context?.message) {
    normalized.message = context.message
  }

  log({
    level: "error",
    message: context?.message ?? normalized.message,
    data: {
      error: normalized.message,
      error_code: serialized.errorCode,
      feature: context?.tags?.feature,
      step: context?.tags?.step,
      ...toLogRecord(context?.extra),
    },
    stack: normalized.stack,
    category: context?.tags?.feature ?? "exception",
  })

  if (!sentryEnabled || shouldSkipCapture(normalized, context)) return

  const scrubbedExtra = scrubValue({
    ...context?.extra,
    error: serialized,
  }) as Record<string, unknown> | undefined
  const scrubbedContexts = context?.contexts
    ? (scrubValue(context.contexts) as Record<string, Record<string, unknown>>)
    : undefined

  getSentry()?.addBreadcrumb({
    category: context?.tags?.feature ?? "exception",
    level: "error",
    message: context?.message ?? normalized.message,
    data: toLogRecord({
      error: serialized,
      feature: context?.tags?.feature,
      step: context?.tags?.step,
      error_code: serialized.errorCode ?? context?.tags?.error_code,
      ...context?.extra,
    }),
  })

  sentryModule?.captureException(normalized, {
    level: context?.level,
    tags: scrubTags(context?.tags),
    extra: scrubbedExtra,
    fingerprint: context?.fingerprint,
    contexts: scrubbedContexts,
  })
}

export function captureMessage(
  message: string,
  options?: {
    level?: SeverityLevel
    tags?: Record<string, string>
    extra?: Record<string, unknown>
    fingerprint?: string[]
    skipDedupe?: boolean
    flush?: boolean
  }
) {
  if (isIgnoredErrorMessage(message)) return

  const fingerprint = buildEventFingerprint([
    "message",
    message,
    options?.tags?.journey,
    options?.tags?.feature,
  ])

  if (!options?.skipDedupe && shouldDedupeEvent(fingerprint)) return

  log({
    level: options?.level ?? "info",
    message,
    category: "message",
  })

  if (!sentryEnabled) return

  sentryModule?.captureMessage(message, {
    level: options?.level,
    tags: scrubTags(options?.tags),
    extra: scrubValue(options?.extra) as Record<string, unknown> | undefined,
    fingerprint: options?.fingerprint,
  })

  if (options?.flush) {
    void sentryModule?.flush(2000)
  }
}

export async function flushMonitor(timeout = 2000) {
  if (!sentryEnabled || !sentryModule) return
  await sentryModule.flush(timeout)
}

export function withScope(callback: (scope: ScopeLike) => void) {
  if (!sentryModule) {
    callback({
      setLevel: () => undefined,
      setExtra: () => undefined,
      setTag: () => undefined,
    })
    return
  }

  sentryModule.withScope(callback)
}
